// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { avoidBreakInsidePrintElement, getSystemColor, mergeWithCss } from "@guestsupply/XDL/Utilities";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import { TypographyProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import { css } from "styled-components";

export interface CartSummarySharedStyles {
    container?: GridContainerProps;
    labelGridItem?: GridItemProps;
    valueGridItem?: GridItemProps;
    titleGridItem?: GridItemProps;
    titleText?: TypographyProps;
    totalLabel?: TypographyProps;
    totalValue?: TypographyProps;
    labelText?: TypographyProps;
    valueText?: TypographyProps;
    dividerGridItem?: GridItemProps;
    AdditionalInfoGridItem?: GridItemProps;
    AdditionalInfoText?: TypographyProps;
}

export const sharedTotalTextStyles: TypographyProps = {
    size: 18,
    weight: "bold",
    css: css`
        letter-spacing: 0;
        line-height: 22px;
        padding: 9px 0 9px 8px;
    `,
};
export const sharedAdditionalInfoTextStyles: TypographyProps = {
    size: 10,
    weight: 400,
    css: css`
        line-height: 11px;
        padding: 0 9px 12px;
    `,
};
export const floatOnPrint = css`
    &&&& {
        @media print {
            float: right;
        }
    }
`;
export const sharedLabelStyles: TypographyProps = {
    size: 13,
    weight: 400,
    css: css`
        display: flex;
        padding-top: 8px;
        padding-left: 9px;
    `,
};
export const sharedValueStyles: TypographyProps = {
    size: 13,
    weight: "bold",
    css: css`
        padding-top: 8px;
        padding-right: 9px;
        margin-left: auto;
        ${floatOnPrint}
    `,
};

const cartSummarySharedStyles: CartSummarySharedStyles = {
    container: {
        gap: 0,
        css: css`
            border: 1px solid ${getColor("common.border")};
            border-radius: 4px;
            &.shipping-summary,
            &.discount-summary {
                padding-bottom: 8px;
            }
            @media print {
                ${avoidBreakInsidePrintElement}
            }
        `,
    },
    labelGridItem: { width: 7.5 as any },
    valueGridItem: { width: 4.5 as any },
    titleGridItem: {
        className: "title-item",
        width: 12,
        css: css`
            border-bottom: 1px solid ${getColor("common.border")};
            background-color: ${getColor("common.ui2")};
            padding: 8px 9px;
            font-size: 18px;
            line-height: 22px;
            border-radius: 4px 4px 0 0;
            @media print {
                background-color: ${getSystemColor("common.ui2")};
            }
        `,
    },
    titleText: {
        variant: "h4",
        as: "p",
        css: css`
            text-align: center;
            margin: 0;
            font-size: 18px;
            line-height: 22px;
        `,
    },
    totalLabel: {
        ...sharedTotalTextStyles,
    },
    totalValue: {
        ...mergeWithCss(sharedTotalTextStyles, {
            css: css`
                padding-left: 0;
                padding-right: 9px;
                margin-left: auto;
                ${floatOnPrint}
            `,
        }),
    },
    labelText: {
        ...sharedLabelStyles,
    },
    valueText: {
        ...sharedValueStyles,
    },
    dividerGridItem: {
        className: "divider-item",
        width: 12,
        css: css`
            &&& {
                border-top: 1px solid ${getColor("common.border")};
                margin: 0 9px;
                margin-top: 8px;
            }
        `,
    },
    AdditionalInfoGridItem: {
        width: 12,
    },
    AdditionalInfoText: {
        ...sharedAdditionalInfoTextStyles,
    },
};

export default cartSummarySharedStyles;
